
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { realtimeCentral } from '@/core/RealtimeCentral';
import { debugLogger } from '@/utils/debugLogger';

interface UsePresenceTimerProps {
  partnerId: string | null;
  conversationId: string | null;
  conversation?: {
    user1_id: string;
    user2_id: string;
    user1_presence_status: string | null;
    user2_presence_status: string | null;
  } | null;
  messages: Array<{ sender_id: string; created_at: string }>;
}

type PresenceStatus = 'online' | 'typing' | 'recording audio' | 'offline';

interface PresenceResult {
  presenceStatus: PresenceStatus;
  lastSeen: string | null;
  mostRecentActivity: string | null;
}

interface PresenceUser {
  userId: string;
  status: string;
  lastUpdate: number;
}

export const usePresenceTimer = ({
  partnerId,
  conversationId,
  conversation,
  messages
}: UsePresenceTimerProps): PresenceResult => {
  const [presenceResult, setPresenceResult] = useState<PresenceResult>({
    presenceStatus: 'offline',
    lastSeen: null,
    mostRecentActivity: null
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const runPresenceCheck = useCallback(async () => {
    if (!partnerId) {
      debugLogger.debug('[usePresenceTimer] Sem partnerId, mantendo offline');
      return;
    }

    debugLogger.debug('[usePresenceTimer] ===== INICIANDO VERIFICAÇÃO TIMER =====');
    debugLogger.debug('[usePresenceTimer] Partner ID:', partnerId);
    debugLogger.debug('[usePresenceTimer] Conversation ID:', conversationId);

    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);

    // ===== PASSO 1: VERIFICAR REALTIME =====
    debugLogger.debug('[usePresenceTimer] PASSO 1: Verificando realtime...');
    try {
      const presenceState = realtimeCentral.getPresenceState();
      debugLogger.debug('[usePresenceTimer] PASSO 1: Estado de presença:', presenceState);
      
      if (presenceState && typeof presenceState === 'object') {
        // Verificar em todos os canais
        for (const [channelKey, channelData] of Object.entries(presenceState)) {
          debugLogger.debug('[usePresenceTimer] PASSO 1: Verificando canal:', channelKey);
          
          if (Array.isArray(channelData)) {
            for (const presence of channelData as PresenceUser[]) {
              if (presence.userId === partnerId) {
                const isRecent = presence.lastUpdate && presence.lastUpdate > fiveMinutesAgo;
                debugLogger.debug('[usePresenceTimer] PASSO 1: Partner encontrado no realtime:', {
                  status: presence.status,
                  lastUpdate: presence.lastUpdate,
                  isRecent,
                  channel: channelKey
                });
                
                // Verificar status especiais primeiro
                if (presence.status === 'typing') {
                  debugLogger.debug('[usePresenceTimer] PASSO 1: ✅ TYPING encontrado');
                  setPresenceResult(prev => ({ ...prev, presenceStatus: 'typing' }));
                  return;
                }
                
                if (presence.status === 'recording audio') {
                  debugLogger.debug('[usePresenceTimer] PASSO 1: ✅ RECORDING encontrado');
                  setPresenceResult(prev => ({ ...prev, presenceStatus: 'recording audio' }));
                  return;
                }
                
                if (presence.status === 'online' && isRecent) {
                  debugLogger.debug('[usePresenceTimer] PASSO 1: ✅ ONLINE encontrado no realtime');
                  setPresenceResult(prev => ({ ...prev, presenceStatus: 'online' }));
                  return;
                }
              }
            }
          }
        }
      }
      
      debugLogger.debug('[usePresenceTimer] PASSO 1: ❌ Não encontrado online no realtime');
    } catch (error) {
      debugLogger.error('[usePresenceTimer] PASSO 1: Erro:', error);
    }

    // ===== PASSO 2: VERIFICAR CONVERSATION STATUS =====
    debugLogger.debug('[usePresenceTimer] PASSO 2: Verificando conversation status...');
    if (conversation) {
      const partnerPresenceField = conversation.user1_id === partnerId 
        ? 'user1_presence_status' 
        : 'user2_presence_status';
      
      const activeStatus = conversation[partnerPresenceField];
      debugLogger.debug('[usePresenceTimer] PASSO 2: Status ativo:', activeStatus);
      
      if (activeStatus === 'typing') {
        debugLogger.debug('[usePresenceTimer] PASSO 2: ✅ TYPING na conversation');
        setPresenceResult(prev => ({ ...prev, presenceStatus: 'typing' }));
        return;
      }
      
      if (activeStatus === 'recording audio') {
        debugLogger.debug('[usePresenceTimer] PASSO 2: ✅ RECORDING na conversation');
        setPresenceResult(prev => ({ ...prev, presenceStatus: 'recording audio' }));
        return;
      }
      
      debugLogger.debug('[usePresenceTimer] PASSO 2: ❌ Sem status ativo na conversation');
    } else {
      debugLogger.debug('[usePresenceTimer] PASSO 2: ❌ Sem dados de conversation');
    }

    // ===== PASSO 3: VERIFICAR PROFILE =====
    debugLogger.debug('[usePresenceTimer] PASSO 3: Verificando profile...');
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_online, last_seen, last_activity_at')
        .eq('id', partnerId)
        .single();

      debugLogger.debug('[usePresenceTimer] PASSO 3: Profile data:', profile);
      debugLogger.debug('[usePresenceTimer] PASSO 3: Profile error:', error);

      if (profile) {
        // Primeira verificação: is_online
        if (profile.is_online === true) {
          debugLogger.debug('[usePresenceTimer] PASSO 3: ✅ PROFILE is_online = TRUE');
          setPresenceResult(prev => ({ 
            ...prev, 
            presenceStatus: 'online',
            lastSeen: profile.last_seen 
          }));
          return;
        }
        
        // Segunda verificação: last_seen < 5 minutos
        if (profile.last_seen) {
          const lastSeenTime = new Date(profile.last_seen).getTime();
          const minutesAgo = Math.floor((now - lastSeenTime) / (1000 * 60));
          debugLogger.debug('[usePresenceTimer] PASSO 3: last_seen check:', {
            lastSeenTime,
            minutesAgo,
            isWithin5Min: lastSeenTime > fiveMinutesAgo
          });
          
          if (lastSeenTime > fiveMinutesAgo) {
            debugLogger.debug('[usePresenceTimer] PASSO 3: ✅ LAST_SEEN dentro de 5 min');
            setPresenceResult(prev => ({ 
              ...prev, 
              presenceStatus: 'online',
              lastSeen: profile.last_seen 
            }));
            return;
          }
        }
        
        // Terceira verificação: last_activity_at < 5 minutos
        if (profile.last_activity_at) {
          const lastActivityTime = new Date(profile.last_activity_at).getTime();
          const minutesAgo = Math.floor((now - lastActivityTime) / (1000 * 60));
          debugLogger.debug('[usePresenceTimer] PASSO 3: last_activity_at check:', {
            lastActivityTime,
            minutesAgo,
            isWithin5Min: lastActivityTime > fiveMinutesAgo
          });
          
          if (lastActivityTime > fiveMinutesAgo) {
            debugLogger.debug('[usePresenceTimer] PASSO 3: ✅ LAST_ACTIVITY dentro de 5 min');
            setPresenceResult(prev => ({ 
              ...prev, 
              presenceStatus: 'online',
              lastSeen: profile.last_seen 
            }));
            return;
          }
        }
        
        debugLogger.debug('[usePresenceTimer] PASSO 3: ❌ Profile não está online recentemente');
        
        // ===== PASSO 4: MOSTRAR "ÚLTIMA VEZ VISTO" =====
        debugLogger.debug('[usePresenceTimer] PASSO 4: Calculando mostRecentActivity...');
        
        // Calcular atividade mais recente das mensagens
        const partnerMessages = messages.filter(msg => msg.sender_id === partnerId);
        const lastPartnerMessage = partnerMessages[partnerMessages.length - 1];
        
        const lastSeenTime = profile.last_seen ? new Date(profile.last_seen).getTime() : 0;
        const lastMessageTime = lastPartnerMessage ? new Date(lastPartnerMessage.created_at).getTime() : 0;
        
        const mostRecentTime = Math.max(lastSeenTime, lastMessageTime);
        const mostRecentDate = mostRecentTime > 0 ? new Date(mostRecentTime).toISOString() : null;
        
        debugLogger.debug('[usePresenceTimer] PASSO 4: Most recent activity:', {
          lastSeenTime,
          lastMessageTime,
          mostRecentTime,
          mostRecentDate
        });
        
        setPresenceResult({
          presenceStatus: 'offline',
          lastSeen: profile.last_seen,
          mostRecentActivity: mostRecentDate
        });
        
        debugLogger.debug('[usePresenceTimer] PASSO 4: ❌ Status final: OFFLINE');
      } else {
        debugLogger.debug('[usePresenceTimer] PASSO 3: ❌ Profile não encontrado');
        setPresenceResult({
          presenceStatus: 'offline',
          lastSeen: null,
          mostRecentActivity: null
        });
      }
    } catch (error) {
      debugLogger.error('[usePresenceTimer] PASSO 3: Erro ao buscar profile:', error);
      setPresenceResult({
        presenceStatus: 'offline',
        lastSeen: null,
        mostRecentActivity: null
      });
    }
  }, [partnerId, conversationId, conversation, messages]);

  // Executar verificação inicial e configurar timer
  useEffect(() => {
    // Executar imediatamente
    runPresenceCheck();
    
    // Configurar timer para executar a cada minuto (60000ms)
    intervalRef.current = setInterval(() => {
      debugLogger.debug('[usePresenceTimer] Timer executando verificação...');
      runPresenceCheck();
    }, 60000);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runPresenceCheck]);

  return presenceResult;
};
